import { useTranslation } from 'react-i18next';

import type { PermittedLocation } from '@cfa/pathway-fe-shared/xp';
import { Button, TextInput } from '@cfa/react-core';

import useAuth from '@/hooks/useAuth';

import { userHasRegulatedLocation } from '@/utils/training-plans/regulated-states';

import Constants from '@/constants';

import type { TranslationKey } from '@/i18n';

const getPlanDescriptionHelperKey = (
  locations: PermittedLocation[] | undefined,
): TranslationKey =>
  userHasRegulatedLocation(locations)
    ? 'TrainingPlans.planDescription.helperRegulated'
    : 'TrainingPlans.planDescription.helperDefault';

interface EditPlanDescriptionProps {
  isReadOnly: boolean | undefined;
  value: string;
  onChange: (value: string) => void;
  isSaveDisabled: boolean;
  isClearing: boolean;
  onSave: () => void | Promise<void>;
}

const EditPlanDescription = ({
  isReadOnly,
  value,
  onChange,
  isSaveDisabled,
  isClearing,
  onSave,
}: EditPlanDescriptionProps) => {
  const { t } = useTranslation();
  const { data: auth } = useAuth();

  return (
    <div className="flex flex-col gap-2" data-testid="PlanDescriptionContainer">
      <div className="flex flex-col md:flex-row md:items-center md:gap-4">
        <TextInput
          aria-label={t('TrainingPlans.planDescription.label')}
          className="w-full [&_textarea]:field-sizing-content [&_textarea]:resize-none"
          data-testid="PlanDescription"
          description={t(getPlanDescriptionHelperKey(auth?.locationsWithRoles))}
          isDisabled={isReadOnly}
          label={t('TrainingPlans.planDescription.label')}
          maxLength={Constants.FORMS.MAX_PLAN_DESCRIPTION_LENGTH}
          multiline
          onChange={onChange}
          placeholder={
            isReadOnly ? '' : t('TrainingPlans.planDescription.placeholder')
          }
          value={value}
        />
        {!isReadOnly && (
          <Button
            className="mt-2 shrink-0 self-end md:self-center"
            color="secondary"
            data-testid="SavePlanDescription"
            isDisabled={isSaveDisabled}
            onPressEnd={onSave}
            size="lg"
            variant={isClearing ? 'outlined' : undefined}
          >
            {t('Button.save')}
          </Button>
        )}
      </div>
    </div>
  );
};

export default EditPlanDescription;
